import { t } from "@lingui/macro";
import { useCallback, useMemo } from "react";
import { useDrawerContext, useSubscriptionContext } from "@WahooFitness/wahoo-offline-mfe";
import { Typography } from "@mui/material";
import { MenuList, MenuListItemVariant, SystmAppIcon } from "@WahooFitness/redesignr";
import useFlaggedFeatures from "@/hooks/useFlaggedFeatures";
import { ClickTarget, EventType, SourcePage } from "@/services/Analytics/EventTypes";
import { useLogEvent } from "@/services/Analytics/useLogEvent";
import { useNavigate } from "react-router-dom";

const SYSTM_CONTENT_DETAILS_URL = "https://systm.wahoofitness.com/content-details/";

const useFitnessAssessmentClickHandler = (sourcePage: SourcePage) => {
  const { userSubscription } = useSubscriptionContext();
  const { setDrawer, handleClose: handleCloseDrawer } = useDrawerContext();
  const { fitnessTestId } = useFlaggedFeatures();
  const { logEvent } = useLogEvent();
  const navigate = useNavigate();

  const logFitnessTestClickEvent = useCallback(() => {
    logEvent("athlete_profile", {
      event_type: EventType.click,
      click_target: ClickTarget.fitness_assessment,
      source_page_name: sourcePage,
    });
  }, [logEvent, sourcePage]);

  const noActiveSub = useMemo(() => {
    return (
      !userSubscription?.status ||
      !["trialing", "active", "failed_payment"].includes(userSubscription?.status)
    );
  }, [userSubscription]);

  const handleFitnessTestClick = () => {
    logFitnessTestClickEvent();
    if (noActiveSub) {
      navigate("/membership?from=fitness_profile&referrer=fitness_assessment");
    } else {
      setDrawer({
        open: true,
        title: t`Fitness Test`,
        children: (
          <MenuList
            listItems={[
              {
                id: "0",
                content: <Typography variant="ui-lg-bold">{t`Open in SYSTM app`}</Typography>,
                secondaryContent: t`For immersive video & audio experience`,
                variant: MenuListItemVariant.ExternalLink,
                action: handleCloseDrawer,
                linkLocation: `${SYSTM_CONTENT_DETAILS_URL}${fitnessTestId}`,
                icon: <SystmAppIcon height={24} width={24} style={{ borderRadius: 4 }} />,
              },
            ]}
          />
        ),
        hidePuller: true,
        hideDivider: true,
        globalBottomPadding: "16px",
      });
    }
  };

  return { handleFitnessTestClick };
};

export default useFitnessAssessmentClickHandler;
